import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, MapPin, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BUDGET_OPTIONS } from "@/config/siteConfig";
import { Reveal, SectionHeading } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const parsePrice = (text) => {
  const m = String(text).replace(/,/g, "").match(/([\d.]+)\s*(cr|crore|lakh|lac|l|k|thousand)?/i);
  if (!m) return null;
  const v = parseFloat(m[1]);
  const unit = (m[2] || "").toLowerCase();
  if (unit.startsWith("cr")) return v * 100;
  if (unit.startsWith("l")) return v;
  if (unit === "k" || unit.startsWith("thousand")) return v / 100;
  return v > 100000 ? v / 100000 : null;
};

const normalizeDb = (p) => ({
  id: p.id,
  title: p.title,
  location: p.location,
  image: p.images?.length ? `${API}/uploads/${p.images[0]}` : null,
  status: p.featured ? "FEATURED" : p.listingType === "Rent" ? "FOR RENT" : "NEW LISTING",
  specs: [p.bhk && `${p.bhk} BHK`, p.areaSqft && `${p.areaSqft} sq.ft.`, p.furnishing].filter(Boolean).join(" • "),
  price: p.price,
  listing: p.listingType === "Rent" ? "rent" : "sale",
  category: p.propertyType === "Plot" ? "Plot" : p.propertyType === "Commercial" ? "Commercial" : "Residential",
  priceNum: parsePrice(p.price),
});

const budgetMatch = (val, priceNum) => {
  if (!val || priceNum == null) return true;
  if (val === "under-50") return priceNum < 50;
  if (val === "50-100") return priceNum >= 50 && priceNum < 100;
  if (val === "100-300") return priceNum >= 100 && priceNum < 300;
  if (val === "300-plus") return priceNum >= 300;
  return true;
};

const applyFilters = (f, source) =>
  source.filter((p) => {
    if (f.lookingFor === "Buy" && p.listing !== "sale") return false;
    if (f.lookingFor === "Rent" && p.listing !== "rent") return false;
    if (f.type && p.category !== f.type) return false;
    if (f.location && !p.location.toLowerCase().includes(f.location.toLowerCase())) return false;
    if (f.budget && p.listing === "sale" && !budgetMatch(f.budget, p.priceNum)) return false;
    return true;
  });

export const Badge = ({ status }) => (
  <span className="absolute top-4 left-4 bg-navy/90 backdrop-blur-sm text-gold text-[10px] font-bold tracking-[0.18em] px-3 py-1.5 rounded-md">
    {status}
  </span>
);

export const FeaturedProperties = ({ filters, onClear, onEnquire }) => {
  const navigate = useNavigate();
  const [dbProps, setDbProps] = useState(null);
  const active = !!filters;

  useEffect(() => {
    axios
      .get(`${API}/properties`)
      .then(({ data }) => setDbProps(data.map(normalizeDb)))
      .catch(() => setDbProps([]));
  }, []);

  const source = dbProps || [];
  const list = active ? applyFilters(filters, source) : source;
  const budgetLabel = filters?.budget ? BUDGET_OPTIONS.find((b) => b.value === filters.budget)?.label : null;

  const enquireMsg = (p) =>
    onEnquire({
      interest: p.listing === "rent" ? "Rent" : "Buy",
      message: `I'm interested in "${p.title}" (${p.location}) — ${p.price}. Please share more details.`,
    });

  return (
    <section id="properties" className="bg-warm py-16 sm:py-24 lg:py-28 mt-12 sm:mt-16" data-testid="featured-properties">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <SectionHeading
            label="Featured Properties"
            title="Properties Worth Exploring"
            subtitle="Explore selected properties for different lifestyles, requirements and budgets."
          />
          <Reveal delay={0.2}>
            <button
              onClick={onClear}
              data-testid="view-all-properties-button"
              className="shrink-0 border border-gold text-navy hover:bg-gold/10 hover:-translate-y-0.5 transition-all duration-300 rounded-lg px-6 py-3.5 text-[14px] font-semibold flex items-center gap-2"
            >
              View All Properties <ArrowRight size={15} />
            </button>
          </Reveal>
        </div>

        {active && (
          <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="active-filters-bar">
            <span className="text-sm text-ink-soft">
              {list.length} {list.length === 1 ? "property" : "properties"} matching
              {filters.lookingFor ? ` · ${filters.lookingFor}` : ""}
              {filters.type ? ` · ${filters.type}` : ""}
              {filters.location ? ` · "${filters.location}"` : ""}
              {budgetLabel ? ` · ${budgetLabel}` : ""}
            </span>
            <button
              onClick={onClear}
              data-testid="clear-filters-button"
              className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-navy bg-gold/20 hover:bg-gold/35 rounded-md px-3 py-1.5 transition-colors"
            >
              <X size={13} /> Clear
            </button>
          </div>
        )}

        {dbProps === null ? (
          <div className="mt-10 sm:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-line overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-line/50" />
                <div className="p-6 space-y-3">
                  <div className="h-5 bg-line/60 rounded w-3/4" />
                  <div className="h-3.5 bg-line/50 rounded w-1/2" />
                  <div className="h-6 bg-line/60 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="mt-14 text-center py-16 bg-white rounded-xl border border-line" data-testid="no-properties-message">
            <p className="font-display text-xl text-navy">
              {active ? "No properties match this search yet." : "New listings are being added."}
            </p>
            <p className="mt-2 text-sm text-ink-soft">
              {active
                ? "Try a different combination, or send us your requirement directly."
                : "Tell us your requirement and we'll share matching properties with you."}
            </p>
            <button
              onClick={active ? onClear : () => onEnquire({ interest: "Buy", message: "I'm looking for a property. Please share available listings with me." })}
              className="mt-6 bg-gold text-navy hover:bg-gold-dark transition-colors rounded-lg px-6 py-3 text-[14px] font-semibold"
              data-testid="no-results-clear-button"
            >
              {active ? "View All Properties" : "Send Your Requirement"}
            </button>
          </div>
        ) : (
          <div className="mt-10 sm:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
            {list.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.1}>
                <article
                  data-testid={`property-card-${p.id}`}
                  className="group bg-white rounded-xl border border-line overflow-hidden hover:shadow-[0_24px_60px_rgba(11,31,51,0.12)] hover:-translate-y-1.5 transition-all duration-400"
                >
                  <button
                    onClick={() => navigate(`/property/${p.id}`)}
                    className="relative block w-full aspect-[4/3] overflow-hidden text-left bg-warm"
                    aria-label={`View ${p.title}`}
                  >
                    {p.image && (
                      <img
                        src={p.image}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      />
                    )}
                    <Badge status={p.status} />
                  </button>
                  <div className="p-6">
                    <h3 className="font-display text-lg sm:text-xl font-semibold text-navy leading-snug">{p.title}</h3>
                    <p className="mt-2 flex items-center gap-1.5 text-[13px] text-ink-soft">
                      <MapPin size={13} className="text-gold" /> {p.location}
                    </p>
                    {p.specs && <p className="mt-3 text-[13px] font-medium text-ink/80 tracking-wide">{p.specs}</p>}
                    <p className="mt-4 font-display text-xl font-semibold text-gold" data-testid={`property-price-${p.id}`}>
                      {p.price}
                    </p>
                    <div className="mt-5 grid grid-cols-2 gap-3">
                      <button
                        onClick={() => navigate(`/property/${p.id}`)}
                        data-testid={`property-card-view-details-${p.id}`}
                        className="border border-navy/20 text-navy hover:border-gold hover:bg-gold/10 transition-colors duration-300 rounded-lg px-4 py-2.5 text-[13px] font-semibold"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => enquireMsg(p)}
                        data-testid={`property-card-enquire-button-${p.id}`}
                        className="bg-gold text-navy hover:bg-gold-dark transition-colors duration-300 rounded-lg px-4 py-2.5 text-[13px] font-semibold"
                      >
                        Enquire
                      </button>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
